import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Sparkles, MessageSquare, Webhook, Copy, CheckCircle2, XCircle, Phone, Workflow, Search, MoreVertical, Paperclip, Smile, CheckCheck, ChevronUp, ChevronDown, X, UserCircle2, ShoppingBag, CalendarDays, Sparkles as SparklesIcon, ClipboardList, FileText, Pin, Archive, BellRing, Tag, Trash2, Mic, Camera, Video, Pause, Play, Download, ArrowLeft } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerClose } from '@/components/ui/drawer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = API_BASE_URL;

const WhatsApp = () => {
  const { t } = useTranslation();
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [showCustomerPanel, setShowCustomerPanel] = useState(false);
  const [customerNotes, setCustomerNotes] = useState('');
  const [conversationMeta, setConversationMeta] = useState({});
  const conversationMetaRef = useRef({});
  const [pendingMedia, setPendingMedia] = useState(null);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [voiceDuration, setVoiceDuration] = useState(0);
  const [voiceRecorder, setVoiceRecorder] = useState(null);
  const [voiceStream, setVoiceStream] = useState(null);
  const [voiceSpeed, setVoiceSpeed] = useState(1);
  const [voicePlaybackProgress, setVoicePlaybackProgress] = useState(0);
  const [isVoicePlaying, setIsVoicePlaying] = useState(false);
  const [cameraMode, setCameraMode] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraRecorder, setCameraRecorder] = useState(null);
  const [cameraChunks, setCameraChunks] = useState([]);
  const [cameraPreviewUrl, setCameraPreviewUrl] = useState('');
  const [cameraPreviewType, setCameraPreviewType] = useState('image');
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [recordingCancelled, setRecordingCancelled] = useState(false);
  const [recordingStartX, setRecordingStartX] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const voiceAudioRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const messageRefs = useRef({});
  const searchInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const lastActionRef = useRef('initial');
  const prevMessageCountRef = useRef(0);
  
  const [waConfig, setWaConfig] = useState({
    whatsapp_number: '',
    kitchen_whatsapp_number: '',
    webhook_url: '',
    webhook_secret: '',
    webhook_secret_configured: false,
    webhook_url_configured: false,
    n8n_workflow_url: '',
    n8n_workflow_url_configured: false,
    connection_status: 'disconnected',
    last_message_at: null,
    messages_count: 0
  });

  const fetchRestaurants = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/restaurants`, { withCredentials: true });
      setRestaurants(data);
      if (data.length > 0) setSelectedRestaurantId(data[0].id);
    } catch (error) { console.error(error); }
  }, []);

  const fetchConversations = useCallback(async (rId) => {
    try {
      const { data } = await axios.get(`${API}/whatsapp/conversations?restaurant_id=${rId}`, { withCredentials: true });
      const visibleConversations = data.filter((conversation) => !conversation?.whatsapp_settings?.deleted);
      setConversations(visibleConversations);
      setConversationMeta((current) => {
        const next = { ...current };
        visibleConversations.forEach((conversation) => {
          const savedMeta = conversation.whatsapp_settings || {};
          next[conversation.customer_id] = {
            ...(current[conversation.customer_id] || { pinned: false, archived: false, unread: false, tags: [], notes: '', vip: false, preferences: {} }),
            pinned: savedMeta.pinned || false,
            archived: savedMeta.archived || false,
            unread: savedMeta.unread || false,
            tags: savedMeta.tags || [],
            notes: savedMeta.notes || '',
            vip: savedMeta.vip || false,
            preferences: savedMeta.preferences || {},
            deleted: savedMeta.deleted || false,
          };
        });
        return next;
      });
      setSelectedConv((current) => {
        if (current) return current;
        return visibleConversations[0] || null;
      });
    } catch (error) { console.error(error); }
  }, []);

  const fetchMessages = useCallback(async (customerId) => {
    try {
      const { data } = await axios.get(`${API}/customers/${customerId}/whatsapp-history`, { withCredentials: true });
      setMessages(data.reverse()); // Show oldest first
    } catch (error) { setMessages([]); }
  }, []);

  const fetchWhatsAppConfig = useCallback(async (rId) => {
    try {
      const { data } = await axios.get(`${API}/restaurants/${rId}/whatsapp-config`, { withCredentials: true });
      setWaConfig({
        ...data,
        webhook_url: data.webhook_url || '',
        n8n_workflow_url: '',
        webhook_secret: ''
      });
    } catch (error) { console.error(error); }
  }, []);

  useEffect(() => { fetchRestaurants(); }, [fetchRestaurants]);
  useEffect(() => {
    if (selectedRestaurantId) {
      fetchConversations(selectedRestaurantId);
      fetchWhatsAppConfig(selectedRestaurantId);
    }
  }, [selectedRestaurantId, fetchConversations, fetchWhatsAppConfig]);

  useEffect(() => {
    conversationMetaRef.current = conversationMeta;
  }, [conversationMeta]);

  useEffect(() => {
    if (selectedConv?.customer_id) fetchMessages(selectedConv.customer_id);
  }, [selectedConv?.customer_id, fetchMessages]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior,
        block: 'end',
      });
    });
  }, []);

  const updateNearBottom = useCallback(() => {
    if (!chatContainerRef.current) {
      isNearBottomRef.current = true;
      return true;
    }

    const container = chatContainerRef.current;
    const threshold = 120;
    const distance = container.scrollHeight - (container.scrollTop + container.clientHeight);
    const nearBottom = distance <= threshold;
    isNearBottomRef.current = nearBottom;

    if (nearBottom) {
      setShowJumpToLatest(false);
    }

    return nearBottom;
  }, []);

  useEffect(() => {
    if (!selectedConv) return;

    lastActionRef.current = 'initial';
    prevMessageCountRef.current = messages.length;
    setShowJumpToLatest(false);
    setShowEmojiPicker(false);
    setShowSearchBar(false);
    setSearchQuery('');
    setActiveMatchIndex(0);

    const timer = window.setTimeout(() => {
      if (lastActionRef.current === 'initial' && messages.length > 0) {
        updateNearBottom();
        scrollToBottom('smooth');
        lastActionRef.current = 'idle';
      }
    }, 120);

    return () => window.clearTimeout(timer);
  }, [selectedConv, messages.length, scrollToBottom, updateNearBottom]);

  useEffect(() => {
    if (!selectedConv || messages.length === 0) {
      prevMessageCountRef.current = messages.length;
      return;
    }

    const hasNewMessages = messages.length > prevMessageCountRef.current;
    const currentAction = lastActionRef.current;

    if (currentAction === 'sent' && hasNewMessages) {
      scrollToBottom('smooth');
      lastActionRef.current = 'idle';
    } else if (currentAction === 'initial') {
      if (messages.length > 0) {
        updateNearBottom();
        scrollToBottom('smooth');
      }
      lastActionRef.current = 'idle';
    } else if (hasNewMessages) {
      if (isNearBottomRef.current) {
        scrollToBottom('smooth');
        setShowJumpToLatest(false);
      } else {
        setShowJumpToLatest(true);
      }
    }

    prevMessageCountRef.current = messages.length;
  }, [messages.length, selectedConv, scrollToBottom, updateNearBottom]);

  useEffect(() => {
    if (!selectedRestaurantId) return;

    const interval = setInterval(() => {
      fetchConversations(selectedRestaurantId);

      if (selectedConv?.customer_id) {
        fetchMessages(selectedConv.customer_id);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [selectedRestaurantId, selectedConv?.customer_id, fetchConversations, fetchMessages]);

  const emojiOptions = ['😊', '😂', '👍', '❤️', '🎉', '🔥', '🙏', '😄', '🤝', '✨'];

  const handleEmojiSelect = (emoji) => {
    setMessage((current) => `${current}${emoji}`);
    setShowEmojiPicker(false);
  };

  const handleSearchToggle = useCallback(() => {
    if (!showSearchBar) {
      setShowSearchBar(true);
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setShowSearchBar(false);
      setSearchQuery('');
      setActiveMatchIndex(0);
    }
  }, [showSearchBar]);

  const handleAttachmentSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const validFiles = files.filter((file) => {
      const isImage = file.type.startsWith('image/');
      const isDocument = /\.(pdf|docx|txt)$/i.test(file.name);
      return isImage || isDocument;
    });

    if (!validFiles.length) {
      toast.error('Only images, PDF, DOCX, and TXT files are supported.');
      return;
    }

    setAttachedFiles(validFiles);
    const previewFile = validFiles[0];
    const isImage = previewFile.type.startsWith('image/');
    setPendingMedia({
      file: previewFile,
      type: isImage ? 'image' : 'document',
      previewUrl: isImage ? URL.createObjectURL(previewFile) : '',
      caption: ''
    });
    toast.success(`${validFiles.length} file${validFiles.length > 1 ? 's' : ''} ready to send.`);
    event.target.value = '';
  };

  const handleComposerKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const formatFileSize = (size) => {
    if (!size) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = size;
    let index = 0;
    while (value >= 1024 && index < units.length - 1) {
      value /= 1024;
      index += 1;
    }
    return `${value.toFixed(value < 10 && index > 0 ? 1 : 0)} ${units[index]}`;
  };

  const resetPendingMedia = () => {
    setPendingMedia(null);
    setCameraPreviewUrl('');
    setCameraPreviewType('image');
    setCameraChunks([]);
    setCameraMode(null);
    setIsCameraActive(false);
  };

  const stopCameraStream = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
    }
    setCameraStream(null);
    setIsCameraActive(false);
  }, [cameraStream]);

  const closeCamera = useCallback(() => {
    stopCameraStream();
    resetPendingMedia();
  }, [stopCameraStream]);

  const openCamera = useCallback(async (mode) => {
    setCameraMode(mode);
    setShowCameraMenu(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: mode !== 'photo' ? { facingMode: 'user' } : true,
        audio: mode === 'video',
      });
      setCameraStream(stream);
      setIsCameraActive(true);
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        cameraVideoRef.current.play().catch(() => {});
      }
    } catch (error) {
      toast.error('Camera access was denied or is unavailable.');
      setCameraMode(null);
    }
  }, []);

  const captureCameraPhoto = useCallback(async () => {
    if (!cameraVideoRef.current || !cameraStream) return;
    const canvas = document.createElement('canvas');
    canvas.width = cameraVideoRef.current.videoWidth || 640;
    canvas.height = cameraVideoRef.current.videoHeight || 480;
    const context = canvas.getContext('2d');
    context.drawImage(cameraVideoRef.current, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setPendingMedia({ file, type: 'image', previewUrl: URL.createObjectURL(file), caption: '' });
      setCameraPreviewType('image');
      setCameraPreviewUrl(URL.createObjectURL(file));
      stopCameraStream();
    }, 'image/jpeg');
  }, [cameraStream, stopCameraStream]);

  const startVideoRecording = useCallback(async () => {
    if (!cameraStream) return;
    const recorder = new MediaRecorder(cameraStream);
    const chunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      const file = new File([blob], `video-${Date.now()}.webm`, { type: 'video/webm' });
      setPendingMedia({ file, type: 'video', previewUrl: URL.createObjectURL(file), caption: '' });
      setCameraPreviewType('video');
      setCameraPreviewUrl(URL.createObjectURL(file));
      setCameraChunks([]);
      stopCameraStream();
    };
    setCameraChunks(chunks);
    setCameraRecorder(recorder);
    recorder.start();
  }, [cameraStream, stopCameraStream]);

  const stopVideoRecording = useCallback(() => {
    if (cameraRecorder && cameraRecorder.state !== 'inactive') {
      cameraRecorder.stop();
    }
    setCameraRecorder(null);
  }, [cameraRecorder]);

  const startVoiceRecording = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Microphone access is not supported in this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const chunks = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
        setAttachedFiles((current) => [...current, file]);
        setPendingMedia({ file, type: 'voice', previewUrl: URL.createObjectURL(file), caption: '' });
        stream.getTracks().forEach((track) => track.stop());
        setVoiceRecorder(null);
        setIsRecordingVoice(false);
        setRecordingCancelled(false);
      };
      mediaRecorder.start();
      setVoiceRecorder(mediaRecorder);
      setVoiceStream(stream);
      setIsRecordingVoice(true);
      setVoiceDuration(0);
      setRecordingCancelled(false);
      setRecordingStartX(null);
    } catch (error) {
      toast.error('Microphone access was denied or is unavailable.');
    }
  }, []);

  const stopVoiceRecording = useCallback(() => {
    if (voiceRecorder && voiceRecorder.state !== 'inactive') {
      voiceRecorder.stop();
    }
    setIsRecordingVoice(false);
  }, [voiceRecorder]);

  const cancelVoiceRecording = useCallback(() => {
    if (voiceRecorder && voiceRecorder.state !== 'inactive') {
      voiceRecorder.stop();
      setRecordingCancelled(true);
    }
    setIsRecordingVoice(false);
    setVoiceDuration(0);
    if (voiceStream) {
      voiceStream.getTracks().forEach((track) => track.stop());
    }
    setVoiceStream(null);
    setVoiceRecorder(null);
  }, [voiceRecorder, voiceStream]);

  const handleVoiceMouseDown = (event) => {
    if (event.button !== 0) return;
    setRecordingStartX(event.clientX);
    startVoiceRecording();
  };

  const handleVoiceMouseUp = () => {
    if (!isRecordingVoice) return;
    if (recordingCancelled) {
      cancelVoiceRecording();
      return;
    }
    stopVoiceRecording();
  };

  const handleVoiceMouseLeave = () => {
    if (!isRecordingVoice) return;
    setRecordingCancelled(true);
    cancelVoiceRecording();
  };

  const handleVoiceTouchStart = (event) => {
    event.preventDefault();
    setRecordingStartX(event.touches[0].clientX);
    startVoiceRecording();
  };

  const handleVoiceTouchEnd = () => {
    if (!isRecordingVoice) return;
    if (recordingCancelled) {
      cancelVoiceRecording();
      return;
    }
    stopVoiceRecording();
  };

  const handleVoiceTouchMove = (event) => {
    if (!isRecordingVoice || recordingStartX === null) return;
    const currentX = event.touches[0].clientX;
    if (currentX < recordingStartX - 80) {
      setRecordingCancelled(true);
      cancelVoiceRecording();
    }
  };

  useEffect(() => {
    if (!isRecordingVoice) return;

    const interval = window.setInterval(() => {
      setVoiceDuration((value) => value + 1);
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isRecordingVoice]);

  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
      if (voiceStream) {
        voiceStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream, voiceStream]);

  const handleSearchNavigation = (direction) => {
    if (!searchResults.length) return;

    const nextIndex = direction === 'next'
      ? (activeMatchIndex + 1) % searchResults.length
      : (activeMatchIndex - 1 + searchResults.length) % searchResults.length;

    setActiveMatchIndex(nextIndex);
    const activeMessage = searchResults[nextIndex];
    const target = messageRefs.current[activeMessage.id];
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const highlightText = (text, query) => {
    if (!query) return text;

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedQuery})`, 'ig'));

    return parts.map((part, index) => (
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={`${part}-${index}`} className="rounded bg-[#E57200]/20 px-1 text-[#E57200]">
          {part}
        </span>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      )
    ));
  };

  const normalizedSearchQuery = useMemo(() => searchQuery.trim().toLowerCase(), [searchQuery]);

  const searchResults = useMemo(() => {
    if (!normalizedSearchQuery) return [];

    return messages.filter((msg) => msg.text?.toLowerCase().includes(normalizedSearchQuery));
  }, [messages, normalizedSearchQuery]);

  useEffect(() => {
    setActiveMatchIndex(0);
  }, [normalizedSearchQuery, selectedConv?.customer_id]);

  useEffect(() => {
    if (!showSearchBar) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleSearchToggle();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearchBar, handleSearchToggle]);

  const handleAISuggest = async () => {
    if (!selectedConv) return;
    const lastMsg = messages.length > 0 ? messages[messages.length - 1].text : '';
    setAiLoading(true);
    try {
      const { data } = await axios.post(`${API}/whatsapp/ai-suggest`, {
        message: lastMsg,
        restaurant_id: selectedRestaurantId
      }, { withCredentials: true });
      setMessage(data.suggestion);
      toast.success('AI suggestion generated');
    } catch (error) {
      toast.error('Failed to get AI suggestion');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSend = async () => {
    const trimmedMessage = message.trim();
    const hasMedia = Boolean(pendingMedia) || attachedFiles.length > 0;
    if ((!trimmedMessage && !hasMedia) || !selectedConv) return;

    try {
      const payload = {
        customer_id: selectedConv.customer_id,
        restaurant_id: selectedRestaurantId,
        text: trimmedMessage || (hasMedia ? 'Media sent' : ''),
      };

      const filesToSend = attachedFiles.length > 0 ? attachedFiles : pendingMedia ? [pendingMedia.file] : [];
      if (filesToSend.length > 0) {
        payload.attachments = filesToSend.map((file) => ({
          name: file.name,
          type: file.type,
          size: file.size,
        }));
      }

      await axios.post(
        `${API}/whatsapp/send`,
        payload,
        { withCredentials: true }
      );

      setMessage('');
      setAttachedFiles([]);
      setPendingMedia(null);
      setShowEmojiPicker(false);
      lastActionRef.current = 'sent';

      fetchMessages(selectedConv.customer_id);
      fetchConversations(selectedRestaurantId);

      toast.success(hasMedia ? 'Media message sent' : 'Message sent');
    } catch (err) {
      console.error(err);
      toast.error('Failed to send message');
    }
  };
  const handleSaveConfig = async () => {
    try {
      const payload = {
        whatsapp_number: waConfig.whatsapp_number,
        kitchen_whatsapp_number: waConfig.kitchen_whatsapp_number,
      };
      if (waConfig.webhook_secret?.trim()) {
        payload.webhook_secret = waConfig.webhook_secret.trim();
      }
      if (waConfig.n8n_workflow_url?.trim()) {
        payload.n8n_workflow_url = waConfig.n8n_workflow_url.trim();
      }
      await axios.patch(`${API}/restaurants/${selectedRestaurantId}/whatsapp-config`, payload, { withCredentials: true });
      toast.success('WhatsApp config saved');
      fetchWhatsAppConfig(selectedRestaurantId);
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleTestWebhook = async () => {
    try {
      if (!waConfig.webhook_url) {
        toast.error('Webhook URL is not configured for this environment');
        return;
      }
      const testPayload = {
        phone: '+1-555-9999',
        name: 'Test Customer',
        text: 'Test message from webhook',
        direction: 'inbound'
      };
      const res = await fetch(waConfig.webhook_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(waConfig.webhook_secret ? { 'X-Webhook-Secret': waConfig.webhook_secret } : {})
        },
        body: JSON.stringify(testPayload)
      });
      if (res.ok) {
        toast.success('Webhook test successful! Refreshing...');
        setTimeout(() => {
          fetchConversations(selectedRestaurantId);
          fetchWhatsAppConfig(selectedRestaurantId);
        }, 1000);
      } else {
        toast.error(`Webhook returned ${res.status}`);
      }
    } catch (error) {
      toast.error('Webhook test failed');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const formatChatTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const getInitials = (name = '') => {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  };

  const buildConversationMeta = useCallback((source = {}) => ({
    pinned: false,
    archived: false,
    unread: false,
    tags: [],
    notes: '',
    vip: false,
    preferences: {},
    deleted: false,
    ...source,
  }), []);

  const getConversationMeta = useCallback((conversation) => {
    if (!conversation?.customer_id) return buildConversationMeta();
    return conversationMeta[conversation.customer_id] || buildConversationMeta();
  }, [buildConversationMeta, conversationMeta]);

  useEffect(() => {
    if (!selectedConv?.customer_id) {
      setCustomerNotes('');
      return;
    }

    const meta = conversationMeta[selectedConv.customer_id] || buildConversationMeta();
    if (customerNotes !== (meta.notes || '')) {
      setCustomerNotes(meta.notes || '');
    }
  }, [selectedConv?.customer_id, conversationMeta, customerNotes, buildConversationMeta]);

  const updateConversationMeta = useCallback((customerId, updates) => {
    if (!customerId) return;

    const nextMeta = buildConversationMeta({
      ...(conversationMetaRef.current[customerId] || {}),
      ...updates,
    });

    conversationMetaRef.current = {
      ...conversationMetaRef.current,
      [customerId]: nextMeta,
    };

    setConversationMeta((current) => ({
      ...current,
      [customerId]: nextMeta,
    }));
  }, [buildConversationMeta]);

  const persistCustomerMetadata = useCallback(async (customerId, updates, options = {}) => {
    if (!customerId) return;

    const previousMeta = buildConversationMeta(conversationMetaRef.current[customerId] || {});
    const nextMeta = buildConversationMeta({
      ...previousMeta,
      ...updates,
    });

    if (updates.preferences) {
      nextMeta.preferences = {
        ...(previousMeta.preferences || {}),
        ...(updates.preferences || {}),
      };
    }

    updateConversationMeta(customerId, nextMeta);

    try {
      const payload = { whatsapp_settings: updates };
      if (updates.preferences) {
        payload.whatsapp_settings.preferences = nextMeta.preferences;
      }
      await axios.patch(`${API}/customers/${customerId}`, payload, { withCredentials: true });
      if (options.successMessage) {
        toast.success(options.successMessage);
      }
    } catch (error) {
      updateConversationMeta(customerId, previousMeta);
      if (options.errorMessage) {
        toast.error(options.errorMessage);
      }
      console.error(error);
    }
  }, [buildConversationMeta, updateConversationMeta]);

  const selectedConversationMeta = selectedConv ? getConversationMeta(selectedConv) : buildConversationMeta();
  const customerTags = selectedConversationMeta.tags?.length ? selectedConversationMeta.tags : ['VIP', 'Repeat Customer', 'Prefers WhatsApp'];
  const summaryPlaceholder = selectedConv
    ? `Customer engagement summary for ${selectedConv.customer_name}.${selectedConversationMeta.vip ? ' VIP customer with saved preferences.' : ' Ready for future backend enrichment.'}`
    : 'Customer summary will appear here once a conversation is selected.';

  const tagOptions = ['VIP', 'New Customer', 'Regular', 'Delivery', 'Pickup'];

  const orderedConversations = useMemo(() => {
    const active = conversations.filter((conversation) => !getConversationMeta(conversation).archived);
    const archived = conversations.filter((conversation) => getConversationMeta(conversation).archived);

    active.sort((left, right) => {
      const leftPinned = getConversationMeta(left).pinned ? 1 : 0;
      const rightPinned = getConversationMeta(right).pinned ? 1 : 0;
      return rightPinned - leftPinned;
    });

    return { active, archived };
  }, [conversations, getConversationMeta]);

  const handleTogglePin = (conversation) => {
    const meta = getConversationMeta(conversation);
    void persistCustomerMetadata(conversation.customer_id, { pinned: !meta.pinned }, {
      successMessage: meta.pinned ? 'Conversation unpinned' : 'Conversation pinned',
      errorMessage: 'Failed to update pin state',
    });
  };

  const handleToggleArchive = (conversation) => {
    const meta = getConversationMeta(conversation);
    const nextArchived = !meta.archived;
    void persistCustomerMetadata(conversation.customer_id, { archived: nextArchived }, {
      successMessage: nextArchived ? 'Conversation archived' : 'Conversation restored',
      errorMessage: 'Failed to update archive state',
    });
    if (selectedConv?.customer_id === conversation.customer_id && nextArchived) {
      setSelectedConv(null);
    }
  };

  const handleToggleUnread = (conversation) => {
    const meta = getConversationMeta(conversation);
    void persistCustomerMetadata(conversation.customer_id, { unread: !meta.unread }, {
      successMessage: meta.unread ? 'Conversation marked as read' : 'Conversation marked as unread',
      errorMessage: 'Failed to update unread state',
    });
  };

  const handleToggleVip = (conversation) => {
    const meta = getConversationMeta(conversation);
    void persistCustomerMetadata(conversation.customer_id, { vip: !meta.vip }, {
      successMessage: meta.vip ? 'VIP status removed' : 'VIP status saved',
      errorMessage: 'Failed to update VIP status',
    });
  };

  const handleToggleTag = (conversation, tag) => {
    const meta = getConversationMeta(conversation);
    const existingTags = meta.tags || [];
    const nextTags = existingTags.includes(tag)
      ? existingTags.filter((item) => item !== tag)
      : [...existingTags, tag];

    void persistCustomerMetadata(conversation.customer_id, { tags: nextTags }, {
      successMessage: existingTags.includes(tag) ? 'Tag removed' : 'Tag added',
      errorMessage: 'Failed to update tag',
    });
  };

  const handleUpdatePreference = (key, value) => {
    if (!selectedConv?.customer_id) return;
    void persistCustomerMetadata(selectedConv.customer_id, {
      preferences: {
        ...(selectedConversationMeta.preferences || {}),
        [key]: value,
      },
    }, {
      successMessage: 'Preference saved',
      errorMessage: 'Failed to save preference',
    });
  };

  const handleCustomerNotesChange = (event) => {
    const nextValue = event.target.value;
    setCustomerNotes(nextValue);
    if (!selectedConv?.customer_id) return;
    void persistCustomerMetadata(selectedConv.customer_id, { notes: nextValue }, {
      successMessage: 'Note saved',
      errorMessage: 'Failed to save note',
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    const previousConversations = conversations;
    const previousMeta = conversationMetaRef.current[deleteTarget.customer_id];
    setConversations((current) => current.filter((conversation) => conversation.customer_id !== deleteTarget.customer_id));
    setConversationMeta((current) => {
      const next = { ...current };
      delete next[deleteTarget.customer_id];
      return next;
    });
    conversationMetaRef.current = {
      ...conversationMetaRef.current,
      [deleteTarget.customer_id]: buildConversationMeta({ deleted: true }),
    };
    if (selectedConv?.customer_id === deleteTarget.customer_id) {
      setSelectedConv(null);
    }
    void persistCustomerMetadata(deleteTarget.customer_id, { deleted: true }, {
      successMessage: 'Conversation removed',
      errorMessage: 'Failed to remove conversation',
    }).catch(() => {
      setConversations(previousConversations);
      if (previousMeta) {
        conversationMetaRef.current = {
          ...conversationMetaRef.current,
          [deleteTarget.customer_id]: previousMeta,
        };
        setConversationMeta((current) => ({
          ...current,
          [deleteTarget.customer_id]: previousMeta,
        }));
      }
    }).finally(() => {
      if (selectedConv?.customer_id === deleteTarget.customer_id) {
        setSelectedConv(null);
      }
    });
    setDeleteTarget(null);
  };

  const openConversation = (conversation) => {
    setSelectedConv(conversation);
    setMobileChatOpen(true);
  };

  return (
    <div className="flex h-[calc(100dvh-6rem)] min-h-0 w-full min-w-0 flex-col md:h-[calc(100dvh-7rem)]" data-testid="whatsapp-page">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 px-1 pb-3">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold tracking-tight md:text-4xl" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>
            {t('whatsapp')}
          </h1>
          <p className="mt-1 hidden text-muted-foreground sm:block" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Manage conversations and n8n integration per restaurant
          </p>
        </div>
        {restaurants.length > 1 && (
          <Select value={selectedRestaurantId || ''} onValueChange={setSelectedRestaurantId}>
            <SelectTrigger className="w-full min-w-0 sm:w-64" data-testid="wa-restaurant-select"><SelectValue placeholder="Select restaurant" /></SelectTrigger>
            <SelectContent>
              {restaurants.map((restaurant) => (
                <SelectItem key={restaurant.id} value={restaurant.id}>{restaurant.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Tabs defaultValue="conversations" className="flex min-h-0 min-w-0 flex-1 flex-col">
        <TabsList className="grid h-auto w-full min-w-0 grid-cols-2 sm:flex sm:w-fit" data-testid="wa-tabs">
          <TabsTrigger className="min-w-0 px-2 sm:px-3" value="conversations" data-testid="tab-conversations">
            <MessageSquare className="me-1.5 h-4 w-4 shrink-0" /> <span className="truncate">Conversations</span>
            {conversations.length > 0 && <Badge className="ms-1.5 shrink-0 px-1.5" style={{ backgroundColor: '#25D366', color: 'white' }}>{conversations.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger className="min-w-0 px-2 sm:px-3" value="integration" data-testid="tab-integration">
            <Webhook className="me-1.5 h-4 w-4 shrink-0" /> <span className="truncate">Integration</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="conversations" className="mt-2 min-h-0 min-w-0 flex-1 overflow-hidden">
          <div className="flex h-full min-h-0 min-w-0 overflow-hidden rounded-lg border border-border/40 bg-background shadow-sm md:flex-row">
            <aside
              className={`${mobileChatOpen ? 'hidden' : 'flex'} min-h-0 min-w-0 w-full flex-col bg-background/80 md:flex md:w-[300px] md:shrink-0 md:border-e lg:w-[320px]`}
              data-testid="conversation-list-panel"
            >
              <div className="flex-none border-b border-border/20 px-4 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Conversations</h2>
                    <p className="mt-1 text-sm text-muted-foreground">Live customer chats</p>
                  </div>
                  <Badge className="rounded-full px-3 py-1 text-xs" style={{ backgroundColor: '#25D366', color: 'white' }}>
                    {conversations.length}
                  </Badge>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto bg-background/70">
                {conversations.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground">
                    <MessageSquare className="mx-auto mb-2 h-12 w-12 opacity-30" />
                    <p className="text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>No conversations yet</p>
                    <p className="mt-2 text-xs">Connect n8n in the Integration tab</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between px-4 py-3">
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Active</span>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setShowArchived((value) => !value)}>
                        {showArchived ? 'Hide' : 'Show'} archived
                      </Button>
                    </div>
                    {orderedConversations.active.map((conv) => {
                      const isSelected = selectedConv?.customer_id === conv.customer_id;
                      const meta = getConversationMeta(conv);
                      const preview = conv.last_message || 'Start a conversation';
                      const timeLabel = conv.last_timestamp ? formatChatTime(conv.last_timestamp) : '';
                      const hasUnread = meta.unread || (conv.unread_count > 0 && conv.last_direction === 'inbound');

                      return (
                        <div key={conv.customer_id} className={`flex min-w-0 items-start gap-1 border-b border-border/40 px-2 py-3 transition-all duration-200 hover:bg-[#E57200]/10 sm:gap-2 sm:px-3 ${isSelected ? 'bg-[#E57200]/10 shadow-sm' : 'bg-transparent'}`}>
                          <button
                            type="button"
                            onClick={() => openConversation(conv)}
                            className="flex min-w-0 flex-1 items-start gap-2 text-left sm:gap-3"
                            data-testid={`conv-${conv.customer_id}`}
                          >
                            <Avatar className="h-10 w-10 shrink-0 border-2 border-white shadow-sm sm:h-11 sm:w-11">
                              <AvatarFallback className="bg-[#25D366] text-sm font-semibold text-white">
                                {getInitials(conv.customer_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex min-w-0 items-center gap-1 font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                  <span className="truncate" dir="auto">{conv.customer_name}</span>
                                  {meta.pinned && <Pin className="h-3.5 w-3.5 shrink-0 text-[#E57200]" />}
                                </div>
                                {timeLabel && <span className="shrink-0 text-[11px] text-muted-foreground">{timeLabel}</span>}
                              </div>
                              <div className="mt-1 truncate text-xs text-muted-foreground" dir="ltr">{conv.phone}</div>
                              <div className="mt-1 flex min-w-0 items-center justify-between gap-2">
                                <p className="min-w-0 flex-1 truncate text-sm text-muted-foreground" dir="auto">{preview}</p>
                                {hasUnread && (
                                  <Badge className="shrink-0 rounded-full px-2 py-0.5 text-[10px]" style={{ backgroundColor: '#25D366', color: 'white' }}>
                                    {conv.unread_count > 0 ? conv.unread_count : '•'}
                                  </Badge>
                                )}
                              </div>
                              {meta.tags?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {meta.tags.map((tag) => (
                                    <Badge key={tag} className="rounded-full bg-[#E57200]/10 px-2 py-0.5 text-[10px] text-[#E57200]">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button aria-label={`Conversation actions for ${conv.customer_name}`} variant="ghost" size="icon" className="h-10 w-10 shrink-0 rounded-full">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onSelect={() => handleTogglePin(conv)}>
                                <Pin className="me-2 h-4 w-4" />
                                {meta.pinned ? 'Unpin conversation' : 'Pin conversation'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => handleToggleArchive(conv)}>
                                <Archive className="me-2 h-4 w-4" />
                                {meta.archived ? 'Restore conversation' : 'Archive conversation'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => handleToggleUnread(conv)}>
                                <BellRing className="me-2 h-4 w-4" />
                                {meta.unread ? 'Mark as read' : 'Mark as unread'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {tagOptions.map((tag) => {
                                const active = (meta.tags || []).includes(tag);
                                return (
                                  <DropdownMenuItem key={tag} onSelect={() => handleToggleTag(conv, tag)}>
                                    <Tag className={`me-2 h-4 w-4 ${active ? 'text-[#E57200]' : 'text-muted-foreground'}`} />
                                    {active ? `Remove ${tag}` : `Add ${tag}`}
                                  </DropdownMenuItem>
                                );
                              })}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => setDeleteTarget(conv)}>
                                <Trash2 className="me-2 h-4 w-4" />
                                Delete conversation
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      );
                    })}

                    {showArchived && orderedConversations.archived.length > 0 && (
                      <div className="border-t border-border/40">
                        <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Archived</div>
                        {orderedConversations.archived.map((conv) => {
                          const meta = getConversationMeta(conv);
                          return (
                            <div key={conv.customer_id} className="flex items-center gap-2 border-b border-border/40 px-3 py-3 opacity-80">
                              <button type="button" onClick={() => openConversation(conv)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                                <Avatar className="h-10 w-10 border border-white shadow-sm">
                                  <AvatarFallback className="bg-muted text-sm font-semibold text-muted-foreground">
                                    {getInitials(conv.customer_name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-medium">{conv.customer_name}</div>
                                  <div className="truncate text-xs text-muted-foreground">{conv.phone}</div>
                                </div>
                              </button>
                              <Button aria-label={`Restore conversation with ${conv.customer_name}`} variant="ghost" size="icon" className="h-10 w-10 shrink-0 rounded-full" onClick={() => handleToggleArchive(conv)}>
                                <Archive className="h-4 w-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </aside>

            <div
              className={`${mobileChatOpen ? 'flex' : 'hidden'} min-h-0 min-w-0 flex-1 flex-col overflow-hidden md:flex`}
              data-testid="active-chat-panel"
            >
              <div className="flex-none border-b border-border/40 bg-gradient-to-r from-[#E57200]/10 via-white to-[#25D366]/10 px-3 py-3">
                <div className="flex min-w-0 items-center justify-between gap-1 sm:gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-full md:hidden"
                    onClick={() => setMobileChatOpen(false)}
                    aria-label="Back to conversations"
                    data-testid="mobile-chat-back"
                  >
                    <ArrowLeft className="h-5 w-5 rtl:rotate-180" />
                  </Button>
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2 text-left sm:gap-3"
                    onClick={() => {
                      setCustomerNotes('');
                      setShowCustomerPanel(true);
                    }}
                  >
                    <Avatar className="h-10 w-10 shrink-0 border-2 border-white shadow-sm sm:h-11 sm:w-11">
                      <AvatarFallback className="bg-[#25D366] font-semibold text-white">
                        {getInitials(selectedConv?.customer_name || 'C')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <h2 className="flex min-w-0 items-center gap-2 text-base font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <span className="truncate" dir="auto">{selectedConv?.customer_name || 'Select a conversation'}</span>
                        {selectedConv && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#25D366]" />}
                      </h2>
                      <div className="truncate text-sm text-muted-foreground" dir="ltr">{selectedConv?.phone || 'Choose a conversation to continue'}</div>
                    </div>
                  </button>
                  <div className="flex shrink-0 items-center gap-0 sm:gap-2">
                    <Button aria-label="Search messages" variant="ghost" size="icon" className="h-10 w-10 rounded-full" onClick={handleSearchToggle}>
                      <Search className="h-4 w-4" />
                    </Button>
                    <Button aria-label="Conversation options" variant="ghost" size="icon" className="h-10 w-10 rounded-full">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex flex-1 min-h-0 flex-col overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(229,114,0,0.06),_transparent_40%),linear-gradient(180deg,_rgba(37,211,102,0.04),_transparent_100%)]">
                {showSearchBar && (
                  <div className="flex-none px-3 pt-3">
                    <div className="mx-auto flex min-w-0 max-w-3xl items-center gap-1 rounded-lg border border-border bg-white/90 px-2 py-2 shadow-sm sm:gap-2 sm:rounded-full sm:px-3">
                      <Search className="h-4 w-4 text-muted-foreground" />
                      <Input
                        ref={searchInputRef}
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search messages"
                        className="h-8 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0"
                      />
                      {searchResults.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <span>{activeMatchIndex + 1}/{searchResults.length}</span>
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" onClick={() => handleSearchNavigation('prev')}>
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" onClick={() => handleSearchNavigation('next')}>
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" onClick={handleSearchToggle}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                <div
                  ref={chatContainerRef}
                  onScroll={updateNearBottom}
                  className="relative min-h-0 min-w-0 flex-1 overflow-y-auto px-2 py-3 pb-4 sm:px-3"
                >
                  <div className="relative mx-auto flex w-full min-w-0 max-w-3xl flex-col gap-2.5">
                    {messages.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-border/60 bg-white/70 p-8 text-center text-muted-foreground shadow-sm">
                        <p className="text-sm">No messages yet</p>
                      </div>
                    ) : messages.map((msg) => {
                      const isCustomer = msg.sender === 'customer';
                      const isManager = msg.sender === 'human' || msg.sender === 'manager';
                      const isMatch = normalizedSearchQuery && msg.text?.toLowerCase().includes(normalizedSearchQuery);

                      return (
                        <div
                          key={msg.id}
                          ref={(element) => {
                            if (element) {
                              messageRefs.current[msg.id] = element;
                            }
                          }}
                          className={`flex ${isCustomer ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`min-w-0 max-w-[88%] rounded-2xl px-3 py-2.5 shadow-sm sm:max-w-[82%] md:max-w-[72%] ${
                              isCustomer
                                ? 'rounded-br-md bg-[#E57200] text-white'
                                : isManager
                                ? 'rounded-bl-md bg-[#25D366] text-white'
                                : 'rounded-bl-md border border-border bg-white text-foreground dark:bg-[#131B2B]'
                            } ${isMatch ? 'ring-2 ring-[#E57200]/30' : ''}`}
                          >
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide opacity-80">
                              {isCustomer ? 'Customer' : isManager ? 'Manager' : 'AI'}
                            </div>
                            {msg.type === 'voice' ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full bg-white/20 text-white" onClick={() => {
                                    setIsVoicePlaying((value) => !value);
                                    setVoicePlaybackProgress((value) => value >= 100 ? 0 : value + 15);
                                  }}>
                                    {isVoicePlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                  </Button>
                                  <div className="flex-1">
                                    <div className="h-1.5 rounded-full bg-white/30">
                                      <div className="h-1.5 rounded-full bg-white" style={{ width: `${voicePlaybackProgress}%` }} />
                                    </div>
                                    <div className="mt-1 flex items-center justify-between text-[11px] opacity-80">
                                      <span>0:12</span>
                                      <button type="button" className="rounded px-1" onClick={() => setVoiceSpeed((value) => value === 1 ? 1.5 : value === 1.5 ? 2 : 1)}>
                                        {voiceSpeed}x
                                      </button>
                                    </div>
                                  </div>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full bg-white/20 text-white">
                                    <Download className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ) : msg.type === 'image' || msg.type === 'video' ? (
                              <div className="space-y-2">
                                {msg.type === 'image' ? (
                                  <div className="overflow-hidden rounded-xl border border-white/20">
                                    <img src={msg.previewUrl || msg.url} alt="Shared media" className="max-h-64 w-full object-cover" />
                                  </div>
                                ) : (
                                  <div className="overflow-hidden rounded-xl border border-white/20 bg-black/20 p-2">
                                    <video src={msg.previewUrl || msg.url} controls className="max-h-64 w-full rounded-lg" />
                                  </div>
                                )}
                              </div>
                            ) : null}
                            {msg.text ? (
                              <div className="mt-2 whitespace-pre-wrap text-sm leading-6 [overflow-wrap:anywhere]" dir="auto">
                                {normalizedSearchQuery ? highlightText(msg.text || '', normalizedSearchQuery) : msg.text}
                              </div>
                            ) : null}
                            <div className={`mt-2 flex items-center gap-1 text-[11px] ${isCustomer ? 'justify-end text-white/80' : 'text-muted-foreground'}`}>
                              <span>{formatChatTime(msg.timestamp)}</span>
                              {isManager && msg.status && <span className="capitalize">{msg.status}</span>}
                              {isManager && msg.status === 'sent' && <CheckCheck className="h-3.5 w-3.5" />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                  {showJumpToLatest && (
                    <Button
                      onClick={() => {
                        setShowJumpToLatest(false);
                        scrollToBottom('smooth');
                      }}
                      className="absolute bottom-4 right-4 rounded-full bg-[#25D366] px-4 py-2 text-sm font-medium text-white shadow-lg hover:bg-[#1fa852]"
                    >
                      Jump to latest
                    </Button>
                  )}
                </div>

                <div className="flex-none border-t border-border/40 bg-background/90 px-3 py-3">
                  {pendingMedia && (
                    <div className="mx-auto mb-2 flex max-w-3xl items-center gap-3 rounded-2xl border border-border bg-white/90 p-2.5 shadow-sm">
                      {pendingMedia.type === 'image' ? (
                        <img src={pendingMedia.previewUrl} alt="Preview" className="h-16 w-16 rounded-xl object-cover" />
                      ) : pendingMedia.type === 'video' ? (
                        <video src={pendingMedia.previewUrl} className="h-16 w-16 rounded-xl object-cover" />
                      ) : (
                        <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-[#25D366]/10 text-[#25D366]">
                          <Mic className="h-7 w-7" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="text-sm font-medium">{pendingMedia.file?.name || 'Attachment ready'}</div>
                        <div className="text-xs text-muted-foreground">{formatFileSize(pendingMedia.file?.size || 0)}</div>
                        {pendingMedia.type !== 'voice' && (
                          <Input
                            placeholder="Add a caption"
                            value={pendingMedia.caption || ''}
                            onChange={(event) => setPendingMedia((current) => (current ? { ...current, caption: event.target.value } : current))}
                            className="mt-2 h-8 border-0 bg-muted/50 shadow-none"
                          />
                        )}
                      </div>
                      <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setPendingMedia(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}

                  {cameraMode && (
                    <div className="mx-auto mb-2 flex max-w-3xl flex-col gap-2.5 rounded-2xl border border-border bg-white/90 p-2.5 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold">{cameraMode === 'photo' ? 'Camera preview' : 'Video preview'}</div>
                          <div className="text-xs text-muted-foreground">Capture a new {cameraMode === 'photo' ? 'photo' : 'video'} to send.</div>
                        </div>
                        <Button variant="ghost" size="icon" className="rounded-full" onClick={closeCamera}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="overflow-hidden rounded-xl border border-border bg-black/90">
                        {cameraStream ? (
                          <video ref={cameraVideoRef} autoPlay playsInline muted className="aspect-video w-full object-cover" />
                        ) : (
                          <div className="flex aspect-video items-center justify-center text-sm text-white">Camera unavailable</div>
                        )}
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        {cameraMode === 'photo' ? (
                          <Button onClick={captureCameraPhoto} className="rounded-full bg-[#E57200] text-white">Take Photo</Button>
                        ) : cameraRecorder ? (
                          <Button onClick={stopVideoRecording} variant="destructive" className="rounded-full">Stop Recording</Button>
                        ) : (
                          <Button onClick={startVideoRecording} className="rounded-full bg-[#E57200] text-white">Start Recording</Button>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mx-auto grid w-full min-w-0 max-w-3xl grid-cols-[2.5rem_2.5rem_minmax(0,1fr)_2.5rem] items-center gap-1 rounded-2xl border border-border bg-white/90 px-1.5 py-1.5 shadow-sm md:flex md:gap-1.5 md:rounded-full md:px-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf,.docx,.txt"
                      className="hidden"
                      onChange={handleAttachmentSelect}
                    />
                    <Button aria-label="Attach file" variant="ghost" size="icon" className="h-10 w-10 rounded-full" onClick={() => fileInputRef.current?.click()}>
                      <Paperclip className="h-4 w-4" />
                    </Button>
                    <div className="relative">
                      <Button aria-label="Camera options" variant="ghost" size="icon" className="h-10 w-10 rounded-full" onClick={() => setShowCameraMenu((value) => !value)}>
                        <Camera className="h-4 w-4" />
                      </Button>
                      {showCameraMenu && (
                        <div className="absolute bottom-12 left-0 flex min-w-[180px] flex-col gap-1 rounded-2xl border border-border bg-white p-2 shadow-xl">
                          <button type="button" className="flex items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => openCamera('photo')}>
                            <Camera className="h-4 w-4" /> Take Photo
                          </button>
                          <button type="button" className="flex items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => openCamera('video')}>
                            <Video className="h-4 w-4" /> Record Video
                          </button>
                          <button type="button" className="flex items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => fileInputRef.current?.click()}>
                            <Paperclip className="h-4 w-4" /> Choose from Gallery
                          </button>
                        </div>
                      )}
                    </div>
                    <Input
                      placeholder="Type a message..."
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      onKeyDown={handleComposerKeyDown}
                      className="col-start-3 row-start-1 h-10 min-w-0 border-0 bg-transparent px-1 shadow-none focus-visible:ring-0 md:flex-1 md:px-3"
                      data-testid="message-input"
                      disabled={!selectedConv}
                    />
                    <div className="relative col-start-1 row-start-2 md:block">
                      <Button aria-label="Choose emoji" variant="ghost" size="icon" className="h-10 w-10 rounded-full" onClick={() => setShowEmojiPicker((value) => !value)}>
                        <Smile className="h-4 w-4" />
                      </Button>
                      {showEmojiPicker && (
                        <div className="absolute bottom-12 right-0 flex max-w-[220px] flex-wrap gap-2 rounded-2xl border border-border bg-white p-3 shadow-xl">
                          {emojiOptions.map((emoji) => (
                            <button
                              key={emoji}
                              type="button"
                              onClick={() => handleEmojiSelect(emoji)}
                              className="rounded-full p-2 text-xl transition hover:bg-muted"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant={isRecordingVoice ? 'destructive' : 'ghost'}
                      size="icon"
                      className="col-start-2 row-start-2 h-10 w-10 rounded-full"
                      aria-label={isRecordingVoice ? 'Stop voice recording' : 'Record voice message'}
                      onMouseDown={handleVoiceMouseDown}
                      onMouseUp={handleVoiceMouseUp}
                      onMouseLeave={handleVoiceMouseLeave}
                      onTouchStart={handleVoiceTouchStart}
                      onTouchEnd={handleVoiceTouchEnd}
                      onTouchMove={handleVoiceTouchMove}
                      disabled={!selectedConv}
                    >
                      <Mic className="h-4 w-4" />
                    </Button>
                    <Button aria-label="Generate AI suggestion" onClick={handleAISuggest} disabled={aiLoading || !selectedConv} variant="outline" size="icon" className="col-start-3 row-start-2 h-10 w-10 justify-self-start rounded-full" data-testid="ai-suggest-btn">
                      <Sparkles className="h-4 w-4" />
                    </Button>
                    <Button aria-label="Send message" onClick={handleSend} disabled={!selectedConv} className="col-start-4 row-start-1 h-10 w-10 rounded-full bg-[#25D366] p-0 text-white hover:bg-[#1fa852]" data-testid="send-btn">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  {isRecordingVoice && (
                    <div className="mx-auto mt-2 flex max-w-3xl items-center justify-between rounded-full border border-[#E57200]/20 bg-[#E57200]/10 px-3 py-1.5 text-sm text-[#E57200] shadow-sm">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-[#E57200]" />
                        Recording {voiceDuration}s
                      </div>
                      <div className="text-xs">Slide left to cancel</div>
                    </div>
                  )}
                  <p className="mx-auto mt-2 hidden max-w-3xl text-xs text-muted-foreground sm:block">AI suggestions powered by your configured prompt. Adjust in AI Bot Settings.</p>
                </div>
              </div>
            </div>

            {showCustomerPanel && (
              <aside className="hidden w-[320px] flex-col border-l border-border/40 bg-background/80 lg:flex">
                <div className="flex-none border-b border-border/20 px-4 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Customer Profile</h2>
                      <p className="mt-1 text-sm text-muted-foreground">Context and notes</p>
                    </div>
                    <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setShowCustomerPanel(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto p-4">
                  <div className="space-y-3">
                    <Card className="border-border/40 shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          <UserCircle2 className="h-4 w-4 text-[#25D366]" />
                          Customer Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex items-center justify-between rounded-xl bg-muted/40 px-2.5 py-2.5">
                          <div>
                            <div className="text-sm font-medium">Name</div>
                            <div className="text-sm text-muted-foreground">{selectedConv?.customer_name || 'Unavailable'}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="rounded-full bg-[#25D366]/10 text-[#25D366]">Active</Badge>
                            <Button size="sm" variant={selectedConversationMeta.vip ? 'default' : 'outline'} className={selectedConversationMeta.vip ? 'bg-[#E57200] text-white hover:bg-[#c95d00]' : ''} onClick={() => handleToggleVip(selectedConv)}>
                              {selectedConversationMeta.vip ? 'VIP' : 'Mark VIP'}
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-2.5 py-2.5 text-sm text-muted-foreground">
                          <Phone className="h-4 w-4" />
                          {selectedConv?.phone || 'Phone number unavailable'}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-border/40 shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          <ClipboardList className="h-4 w-4 text-[#E57200]" />
                          Customer Statistics
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid gap-2">
                        <div className="rounded-xl bg-muted/40 p-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <ShoppingBag className="h-4 w-4" />
                            Total Orders
                          </div>
                          <div className="mt-2 text-xl font-semibold">12</div>
                        </div>
                        <div className="rounded-xl bg-muted/40 p-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <FileText className="h-4 w-4" />
                            Total Spending
                          </div>
                          <div className="mt-2 text-xl font-semibold">$248</div>
                        </div>
                        <div className="rounded-xl bg-muted/40 p-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <CalendarDays className="h-4 w-4" />
                            Last Order Date
                          </div>
                          <div className="mt-2 text-sm font-semibold">Today</div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-border/40 shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          <SparklesIcon className="h-4 w-4 text-[#25D366]" />
                          Tags
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex flex-wrap gap-2">
                        {customerTags.map((tag) => (
                          <Badge key={tag} className="rounded-full bg-[#E57200]/10 px-3 py-1 text-[#E57200]">
                            {tag}
                          </Badge>
                        ))}
                      </CardContent>
                    </Card>

                    <Card className="border-border/40 shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          <FileText className="h-4 w-4 text-[#E57200]" />
                          Notes
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          placeholder="Add a note about this customer..."
                          value={customerNotes}
                          onChange={handleCustomerNotesChange}
                          className="min-h-[110px] resize-none"
                        />
                        <p className="mt-2 text-xs text-muted-foreground">Prepared for future backend integration.</p>
                      </CardContent>
                    </Card>

                    <Card className="border-border/40 shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          <SparklesIcon className="h-4 w-4 text-[#25D366]" />
                          Preferences
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant={selectedConversationMeta.preferences?.prefers_whatsapp ? 'default' : 'outline'}
                          className={selectedConversationMeta.preferences?.prefers_whatsapp ? 'bg-[#25D366] text-white hover:bg-[#1fa852]' : ''}
                          onClick={() => handleUpdatePreference('prefers_whatsapp', !selectedConversationMeta.preferences?.prefers_whatsapp)}
                        >
                          Prefers WhatsApp
                        </Button>
                        <Button
                          size="sm"
                          variant={selectedConversationMeta.preferences?.receives_promos ? 'default' : 'outline'}
                          className={selectedConversationMeta.preferences?.receives_promos ? 'bg-[#25D366] text-white hover:bg-[#1fa852]' : ''}
                          onClick={() => handleUpdatePreference('receives_promos', !selectedConversationMeta.preferences?.receives_promos)}
                        >
                          Receives promos
                        </Button>
                      </CardContent>
                    </Card>

                    <Card className="border-border/40 shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          <SparklesIcon className="h-4 w-4 text-[#25D366]" />
                          AI Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="rounded-xl border border-dashed border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground">
                          {summaryPlaceholder}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </aside>
            )}
          </div>
        </TabsContent>

        <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
              <AlertDialogDescription>
                This action will remove the conversation from the list. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleConfirmDelete}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="lg:hidden">
          <Drawer open={showCustomerPanel} onOpenChange={setShowCustomerPanel}>
            <DrawerContent className="max-h-[90vh] overflow-y-auto">
              <DrawerHeader className="border-b border-border/40 pb-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-14 w-14 border-2 border-white shadow-sm">
                      <AvatarFallback className="bg-[#25D366] text-lg font-semibold text-white">
                        {selectedConv ? getInitials(selectedConv.customer_name) : 'C'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <DrawerTitle className="text-left text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {selectedConv?.customer_name || 'Customer Profile'}
                      </DrawerTitle>
                      <DrawerDescription className="mt-1 text-left">
                        {selectedConv?.phone || 'Customer details and context'}
                      </DrawerDescription>
                    </div>
                  </div>
                  <DrawerClose asChild>
                    <Button variant="ghost" size="icon" className="rounded-full">
                      <X className="h-4 w-4" />
                    </Button>
                  </DrawerClose>
                </div>
              </DrawerHeader>

              <div className="space-y-3 p-4">
                <Card className="border-border/40 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <UserCircle2 className="h-4 w-4 text-[#25D366]" />
                      Customer Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between rounded-xl bg-muted/40 px-2.5 py-2.5">
                      <div>
                        <div className="text-sm font-medium">Name</div>
                        <div className="text-sm text-muted-foreground">{selectedConv?.customer_name || 'Unavailable'}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="rounded-full bg-[#25D366]/10 text-[#25D366]">Active</Badge>
                        <Button size="sm" variant={selectedConversationMeta.vip ? 'default' : 'outline'} className={selectedConversationMeta.vip ? 'bg-[#E57200] text-white hover:bg-[#c95d00]' : ''} onClick={() => handleToggleVip(selectedConv)}>
                          {selectedConversationMeta.vip ? 'VIP' : 'Mark VIP'}
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-2.5 py-2.5 text-sm text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      {selectedConv?.phone || 'Phone number unavailable'}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/40 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <ClipboardList className="h-4 w-4 text-[#E57200]" />
                      Customer Statistics
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-2 sm:grid-cols-3">
                    <div className="rounded-xl bg-muted/40 p-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <ShoppingBag className="h-4 w-4" />
                        Total Orders
                      </div>
                      <div className="mt-2 text-xl font-semibold">12</div>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <FileText className="h-4 w-4" />
                        Total Spending
                      </div>
                      <div className="mt-2 text-xl font-semibold">$248</div>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <CalendarDays className="h-4 w-4" />
                        Last Order Date
                      </div>
                      <div className="mt-2 text-sm font-semibold">Today</div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/40 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <SparklesIcon className="h-4 w-4 text-[#25D366]" />
                      Tags
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-2">
                    {customerTags.map((tag) => (
                      <Badge key={tag} className="rounded-full bg-[#E57200]/10 px-3 py-1 text-[#E57200]">
                        {tag}
                      </Badge>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-border/40 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <FileText className="h-4 w-4 text-[#E57200]" />
                      Notes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      placeholder="Add a note about this customer..."
                      value={customerNotes}
                      onChange={(event) => setCustomerNotes(event.target.value)}
                      className="min-h-[110px] resize-none"
                    />
                    <p className="mt-2 text-xs text-muted-foreground">Prepared for future backend integration.</p>
                  </CardContent>
                </Card>

                <Card className="border-border/40 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <SparklesIcon className="h-4 w-4 text-[#25D366]" />
                      Preferences
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant={selectedConversationMeta.preferences?.prefers_whatsapp ? 'default' : 'outline'}
                      className={selectedConversationMeta.preferences?.prefers_whatsapp ? 'bg-[#25D366] text-white hover:bg-[#1fa852]' : ''}
                      onClick={() => handleUpdatePreference('prefers_whatsapp', !selectedConversationMeta.preferences?.prefers_whatsapp)}
                    >
                      Prefers WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedConversationMeta.preferences?.receives_promos ? 'default' : 'outline'}
                      className={selectedConversationMeta.preferences?.receives_promos ? 'bg-[#25D366] text-white hover:bg-[#1fa852]' : ''}
                      onClick={() => handleUpdatePreference('receives_promos', !selectedConversationMeta.preferences?.receives_promos)}
                    >
                      Receives promos
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-border/40 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <SparklesIcon className="h-4 w-4 text-[#25D366]" />
                      AI Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-xl border border-dashed border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground">
                      {summaryPlaceholder}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </DrawerContent>
          </Drawer>
        </div>

        <TabsContent value="integration" className="min-w-0 overflow-y-auto">
          <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
            <Card className="border-border/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Workflow className="h-5 w-5" style={{ color: '#E57200' }} />
                  Connection Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex min-w-0 flex-col gap-3 rounded-lg bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="text-sm text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>n8n Connection</div>
                    <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {waConfig.connection_status === 'connected' ? (
                        <span className="flex items-center gap-2 text-[#10B981]"><CheckCircle2 className="h-6 w-6" /> Connected</span>
                      ) : (
                        <span className="flex items-center gap-2 text-muted-foreground"><XCircle className="h-6 w-6" /> Disconnected</span>
                      )}
                    </div>
                  </div>
                  <Badge className="w-fit shrink-0" style={{ backgroundColor: waConfig.connection_status === 'connected' ? '#10B981' : '#64748B', color: 'white', fontSize: '0.75rem' }}>
                    {waConfig.messages_count} messages
                  </Badge>
                </div>
                {waConfig.last_message_at && (
                  <div className="text-sm text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    Last message: {new Date(waConfig.last_message_at).toLocaleString()}
                  </div>
                )}
                <Button onClick={handleTestWebhook} variant="outline" className="w-full" data-testid="test-webhook-btn">
                  Send Test Webhook
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Phone className="h-5 w-5" style={{ color: '#E57200' }} />
                  WhatsApp Numbers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Customer-facing WhatsApp Number</Label>
                  <Input placeholder="+1234567890" value={waConfig.whatsapp_number || ''} onChange={(event) => setWaConfig({ ...waConfig, whatsapp_number: event.target.value })} data-testid="wa-customer-number" />
                </div>
                <div>
                  <Label>Kitchen Notification Number</Label>
                  <Input placeholder="+1234567890" value={waConfig.kitchen_whatsapp_number || ''} onChange={(event) => setWaConfig({ ...waConfig, kitchen_whatsapp_number: event.target.value })} data-testid="wa-kitchen-number" />
                  <p className="mt-1 text-xs text-muted-foreground">Receives new order alerts</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/40 lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Webhook className="h-5 w-5" style={{ color: '#E57200' }} />
                  n8n Webhook Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Inbound Webhook URL (Set this in your n8n WhatsApp trigger)</Label>
                  <div className="mt-1 flex min-w-0 gap-2">
                    <Input readOnly value={waConfig.webhook_url} className="min-w-0 bg-muted font-mono text-sm" data-testid="webhook-url" />
                    <Button aria-label="Copy webhook URL" className="shrink-0" variant="outline" size="icon" onClick={() => copyToClipboard(waConfig.webhook_url, 'Webhook URL')} data-testid="copy-webhook-btn">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  {!waConfig.webhook_url_configured && (
                    <p className="mt-2 text-xs text-amber-700">
                      Public backend callback URL is not explicitly configured for this environment.
                    </p>
                  )}
                  <p className="mt-2 text-xs text-muted-foreground">
                    Point your n8n WhatsApp trigger HTTP node to this URL. Expected JSON payload:
                  </p>
                  <pre className="mt-2 whitespace-pre-wrap break-all rounded bg-[#131B2B] p-3 text-xs text-[#10B981]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{`{
  "phone": "+1234567890",
  "name": "Customer Name",
  "text": "Hello, I want to order",
  "direction": "inbound"
}`}</pre>
                </div>

                <div>
                  <Label>Webhook Secret (Optional - sent in X-Webhook-Secret header)</Label>
                  <Input type="password" placeholder="random-secret-string" value={waConfig.webhook_secret || ''} onChange={(event) => setWaConfig({ ...waConfig, webhook_secret: event.target.value })} data-testid="webhook-secret" />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Existing secret is never shown. Enter a new value only to replace it.
                    {waConfig.webhook_secret_configured && <Badge className="ms-2" style={{ backgroundColor: '#10B981', color: 'white' }}>Configured</Badge>}
                  </p>
                </div>

                <div>
                  <Label>n8n Workflow URL (Restaurant-scoped outbound automation)</Label>
                  <Input placeholder="https://your-n8n.com/webhook/send-whatsapp" value={waConfig.n8n_workflow_url || ''} onChange={(event) => setWaConfig({ ...waConfig, n8n_workflow_url: event.target.value })} data-testid="n8n-workflow-url" />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Existing routing URL is never shown. Enter a new value only to replace it.
                    {waConfig.n8n_workflow_url_configured && <Badge className="ms-2" style={{ backgroundColor: '#10B981', color: 'white' }}>Configured</Badge>}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">When configured, outbound restaurant events such as human replies and order-status updates POST only to this restaurant's URL.</p>
                </div>

                <Button onClick={handleSaveConfig} style={{ backgroundColor: '#E57200' }} data-testid="save-wa-config">Save Configuration</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WhatsApp;
